import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MobileLayout from "../components/layout/MobileLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import { Wallet, Copy, IndianRupee, Smartphone } from "lucide-react";

const QUICK_AMOUNTS = [100, 200, 500, 1000, 10000, 20000, 50000];

export default function Funds() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState({});
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "withdraw" ? "withdraw" : "deposit";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => { api.get("/settings").then(({data}) => setSettings(data)); }, []);
  useEffect(() => { setParams({ tab }, { replace: true }); /* eslint-disable-line */ }, [tab]);

  return (
    <MobileLayout>
      <Toaster richColors position="top-center" />
      <div className="bg-gradient-to-br from-[#FF7A00] to-[#F5A623] text-white rounded-2xl p-5 mb-4">
        <div className="text-[11px] uppercase tracking-widest opacity-80">Wallet Balance</div>
        <div className="font-display font-bold text-4xl tabular-nums mt-1" data-testid="wallet-balance-display">{user?.wallet_balance ?? 0}</div>
        <div className="text-xs opacity-80 mt-1">points</div>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="funds-tabs">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="deposit" data-testid="tab-deposit">Add Funds</TabsTrigger>
          <TabsTrigger value="withdraw" data-testid="tab-withdraw">Withdraw</TabsTrigger>
        </TabsList>
        <TabsContent value="deposit">
          <DepositForm settings={settings} onDone={refreshUser} />
        </TabsContent>
        <TabsContent value="withdraw">
          <WithdrawForm settings={settings} onDone={refreshUser} />
        </TabsContent>
      </Tabs>
    </MobileLayout>
  );
}

function buildUpiUrl({ upi_id, payee, amount, note }) {
  const params = new URLSearchParams({
    pa: upi_id,
    pn: payee || "Reddy Club",
    am: String(amount),
    cu: "INR",
    tn: note || "Deposit",
  });
  return `upi://pay?${params.toString()}`;
}

function DepositForm({ settings, onDone }) {
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const upi = settings.upi_id || "m11clube@upi";
  const payee = settings.upi_payee_name || "Reddy Club";
  const min = settings.min_deposit || 100;
  const num = parseInt(amount) || 0;

  const copy = async (text, label = "Copied") => {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const payNow = () => {
    if (num < min) { toast.error(`Min deposit ${min}`); return; }
    const url = buildUpiUrl({ upi_id: upi, payee, amount: num, note: `Deposit-${num}` });
    // Android wrapper (WebView) intercepts `upi://pay` and shows the native UPI app chooser.
    // On desktop / non-supporting browsers this navigation will simply do nothing.
    window.location.href = url;
    setPaid(true);
  };

  const submit = async () => {
    if (num < min) return toast.error(`Min deposit ${min}`);
    if (!utr || utr.length < 6) return toast.error("Enter valid UTR / transaction reference");
    setLoading(true);
    try {
      await api.post("/wallet/deposit", { amount: num, utr, method: "upi" });
      toast.success("Deposit request submitted. Funds credited after admin verification.");
      setAmount(""); setUtr(""); setPaid(false); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="premium-card p-4" data-testid="deposit-form">
      <Label className="text-xs uppercase tracking-wider text-cyan-200/80">Select Amount</Label>
      <div className="grid grid-cols-4 gap-2 mt-1.5 mb-3" data-testid="quick-amounts">
        {QUICK_AMOUNTS.map(a => (
          <button key={a} onClick={() => setAmount(String(a))} data-testid={`qa-${a}`}
            className={`text-sm font-semibold py-2 rounded-md border transition tabular-nums ${num === a ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-black/40 text-cyan-100 border-cyan-400/25 hover:border-[#FF7A00] hover:text-[#FF7A00]"}`}>
            {a >= 1000 ? `${a/1000}K` : a}
          </button>
        ))}
      </div>

      <Label className="text-xs uppercase tracking-wider text-cyan-200/80">Or enter custom amount</Label>
      <div className="relative mt-1.5">
        <IndianRupee className="w-4 h-4 absolute left-3 top-3.5 text-cyan-200/60" />
        <Input data-testid="deposit-amount" inputMode="numeric" value={amount} onChange={(e)=>{setAmount(e.target.value.replace(/\D/g,"")); setPaid(false);}} className="pl-9 h-11 text-base font-semibold" placeholder={`Minimum ${min}`} />
      </div>

      {/* Pay via UPI: single button — Android wrapper intercepts upi:// and shows native app chooser */}
      <Button
        data-testid="pay-upi-btn"
        onClick={payNow}
        disabled={num < min}
        className="w-full mt-4 h-12 btn-brand text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Smartphone className="w-5 h-5 mr-2" /> Pay {num >= min ? `₹${num}` : ""} via UPI
      </Button>
      <div className="mt-2 text-[11px] text-cyan-200/70 text-center">
        Paying to <button onClick={() => copy(upi, "UPI ID copied")} className="font-semibold text-cyan-100/90 inline-flex items-center gap-1 hover:text-[#FF7A00]" data-testid="upi-id">{upi} <Copy className="w-3 h-3" /></button>
      </div>

      {/* After payment, user submits UTR */}
      {paid && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="paid-banner">
          <div className="text-xs text-emerald-900 mb-2 font-medium">After completing payment in your UPI app, paste the UTR / Transaction ID below.</div>
        </div>
      )}

      <Label className="text-xs uppercase tracking-wider text-cyan-200/80 mt-4 block">UTR / Transaction Reference</Label>
      <Input data-testid="deposit-utr" value={utr} onChange={(e)=>setUtr(e.target.value)} className="mt-1.5 h-11" placeholder="12-digit UTR from your UPI app" />

      <Button data-testid="deposit-submit" onClick={submit} disabled={loading || !utr} className="w-full mt-4 h-11 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50">
        <Wallet className="w-4 h-4 mr-1.5" /> {loading ? "Submitting…" : "Submit Deposit Request"}
      </Button>
      <p className="text-[10px] text-cyan-200/60 mt-2 leading-relaxed text-center">Wallet credited after admin verification (usually under 30 min).</p>
    </div>
  );
}

function WithdrawForm({ settings, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [upi, setUpi] = useState("");
  const [bank, setBank] = useState({ holder_name: "", bank_name: "", account_number: "", ifsc: "" });
  const [loading, setLoading] = useState(false);
  const minW = settings.min_withdraw ?? 500;
  const wo = settings.withdraw_open_time;
  const wc = settings.withdraw_close_time;
  const num = parseInt(amount) || 0;

  const submit = async () => {
    if (num < minW) return toast.error(`Min withdraw is ${minW}`);
    const body = { amount: num, method };
    if (method === "upi") {
      if (upi.length < 4) return toast.error("Enter valid UPI ID");
      body.upi_id = upi;
    } else {
      if (!bank.holder_name || !bank.bank_name || !bank.account_number || !bank.ifsc) return toast.error("Fill all bank details");
      Object.assign(body, bank);
    }
    setLoading(true);
    try {
      await api.post("/wallet/withdraw", body);
      toast.success("Withdrawal request submitted.");
      setAmount(""); setUpi(""); setBank({ holder_name: "", bank_name: "", account_number: "", ifsc: "" });
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="premium-card p-4" data-testid="withdraw-form">
      {wo && wc && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-lg p-2 mb-3 text-center">
          Withdrawals open {wo} → {wc} (IST)
        </div>
      )}

      <Label className="text-xs uppercase tracking-wider text-cyan-200/80">Quick Amount</Label>
      <div className="grid grid-cols-4 gap-2 mt-1.5 mb-3" data-testid="qa-withdraw">
        {QUICK_AMOUNTS.filter(a => a >= 500).map(a => (
          <button key={a} onClick={() => setAmount(String(a))}
            className={`text-sm font-semibold py-2 rounded-md border transition tabular-nums ${num === a ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-black/40 text-cyan-100 border-cyan-400/25 hover:border-[#FF7A00] hover:text-[#FF7A00]"}`}>
            {a >= 1000 ? `${a/1000}K` : a}
          </button>
        ))}
      </div>

      <Label className="text-xs uppercase tracking-wider text-cyan-200/80">Custom Amount</Label>
      <div className="relative mt-1.5">
        <IndianRupee className="w-4 h-4 absolute left-3 top-3.5 text-cyan-200/60" />
        <Input data-testid="withdraw-amount" inputMode="numeric" value={amount} onChange={(e)=>setAmount(e.target.value.replace(/\D/g,""))} className="pl-9 h-11" placeholder={`Min ${minW}`} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2" data-testid="withdraw-method">
        <button onClick={()=>setMethod("upi")} className={`text-sm font-medium py-2.5 rounded-lg border transition ${method==="upi" ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-black/40 text-cyan-100 border-cyan-400/25"}`} data-testid="method-upi">UPI</button>
        <button onClick={()=>setMethod("bank")} className={`text-sm font-medium py-2.5 rounded-lg border transition ${method==="bank" ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-black/40 text-cyan-100 border-cyan-400/25"}`} data-testid="method-bank">Bank Transfer</button>
      </div>

      {method === "upi" ? (
        <div className="mt-3">
          <Label className="text-xs uppercase tracking-wider text-cyan-200/80">Your UPI ID</Label>
          <Input data-testid="withdraw-upi" value={upi} onChange={(e)=>setUpi(e.target.value)} className="mt-1.5 h-11" placeholder="yourname@bank" autoComplete="off" />
        </div>
      ) : (
        <div className="mt-3 space-y-2.5">
          <div><Label className="text-xs uppercase tracking-wider text-cyan-200/80">Account Holder Name</Label><Input data-testid="wd-holder" value={bank.holder_name} onChange={(e)=>setBank({...bank, holder_name: e.target.value})} className="mt-1.5 h-11" autoComplete="off" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-cyan-200/80">Bank Name</Label><Input data-testid="wd-bank-name" value={bank.bank_name} onChange={(e)=>setBank({...bank, bank_name: e.target.value})} className="mt-1.5 h-11" autoComplete="off" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-cyan-200/80">Account Number</Label><Input data-testid="wd-account" inputMode="numeric" value={bank.account_number} onChange={(e)=>setBank({...bank, account_number: e.target.value.replace(/\D/g,"")})} className="mt-1.5 h-11 tabular-nums" autoComplete="off" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-cyan-200/80">IFSC Code</Label><Input data-testid="wd-ifsc" value={bank.ifsc} onChange={(e)=>setBank({...bank, ifsc: e.target.value.toUpperCase()})} className="mt-1.5 h-11 uppercase tracking-wider" autoComplete="off" /></div>
        </div>
      )}

      <Button data-testid="withdraw-submit" onClick={submit} disabled={loading} className="w-full mt-4 h-11 bg-slate-900 hover:bg-slate-800 text-white">{loading ? "Submitting…" : "Request Withdrawal"}</Button>
    </div>
  );
}
